import { useState, useEffect } from 'react'
import {
  Box,
  Heading,
  Text,
  VStack,
  Alert,
  AlertIcon,
  Code,
  Button,
  HStack,
  Badge,
} from '@chakra-ui/react'
import { CheckCircle, XCircle, AlertTriangle, Clock } from 'lucide-react'
import { useCSPNonce } from '@/hooks/useCSPNonce'
import { useThemeColors } from '@/hooks/useThemeColors'

interface TestResult {
  name: string
  status: 'pass' | 'fail' | 'warning'
  message: string
  details?: string
}

export default function TestCSPPage() {
  const colors = useThemeColors()
  const [testResults, setTestResults] = useState<TestResult[]>([])
  const [isRunning, setIsRunning] = useState(false)
  const [cspHeader, setCspHeader] = useState('')
  const nonce = useCSPNonce()

  const runTests = async () => {
    setIsRunning(true)
    setTestResults([])

    const results: TestResult[] = []

    try {
      const response = await fetch(window.location.href, { method: 'HEAD' })
      const csp = response.headers.get('Content-Security-Policy')
      setCspHeader(csp || 'Not found')

      if (csp) {
        results.push({
          name: 'CSP Header Present',
          status: 'pass',
          message: 'Content-Security-Policy header is present',
          details: csp,
        })
      } else {
        results.push({
          name: 'CSP Header Present',
          status: 'fail',
          message: 'Content-Security-Policy header is missing',
        })
      }
    } catch (error) {
      results.push({
        name: 'CSP Header Present',
        status: 'fail',
        message: 'Failed to fetch CSP header',
        details: String(error),
      })
    }

    if (nonce) {
      results.push({
        name: 'CSP Nonce Available',
        status: 'pass',
        message: 'CSP nonce is available',
        details: nonce,
      })
    } else {
      results.push({
        name: 'CSP Nonce Available',
        status: 'warning',
        message: 'CSP nonce not available (may be normal in development)',
      })
    }

    try {
      // eslint-disable-next-line no-eval
      eval('console.log("CSP Test: Eval executed successfully")')
      results.push({
        name: 'Eval Execution',
        status: 'pass',
        message: 'Eval is allowed (development mode)',
      })
    } catch (error) {
      results.push({
        name: 'Eval Execution',
        status: 'fail',
        message: 'Eval is blocked by CSP',
        details: String(error),
      })
    }

    try {
      const style = document.createElement('style')
      style.textContent = 'body { background: linear-gradient(45deg, #f0f0f0, #e0e0e0) !important; }'
      document.head.appendChild(style)

      const computedStyle = window.getComputedStyle(document.body)
      if (computedStyle.background.includes('linear-gradient')) {
        results.push({
          name: 'Inline Style',
          status: 'pass',
          message: 'Inline styles are allowed (development mode)',
        })
      } else {
        results.push({
          name: 'Inline Style',
          status: 'warning',
          message: 'Inline style created but may not be applied',
        })
      }
    } catch (error) {
      results.push({
        name: 'Inline Style',
        status: 'fail',
        message: 'Inline styles are blocked by CSP',
        details: String(error),
      })
    }

    try {
      const script = document.createElement('script')
      script.src = 'https://example.com/test-script.js'
      script.onerror = () => {
        results.push({
          name: 'External Script',
          status: 'pass',
          message: 'External scripts are blocked by CSP (security working)',
        })
      }
      script.onload = () => {
        results.push({
          name: 'External Script',
          status: 'fail',
          message: 'External scripts are allowed (security risk)',
        })
      }
      document.head.appendChild(script)

      setTimeout(() => {
        if (!results.find((r) => r.name === 'External Script')) {
          results.push({
            name: 'External Script',
            status: 'warning',
            message: 'External script test timed out',
          })
        }
      }, 2000)
    } catch (error) {
      results.push({
        name: 'External Script',
        status: 'pass',
        message: 'External scripts are blocked by CSP',
        details: String(error),
      })
    }

    try {
      const script = document.createElement('script')
      script.src = 'data:text/javascript,console.log("CSP Test: Data URI script")'
      script.onerror = () => {
        results.push({
          name: 'Data URI Script',
          status: 'pass',
          message: 'Data URI scripts are blocked by CSP (security working)',
        })
      }
      script.onload = () => {
        results.push({
          name: 'Data URI Script',
          status: 'fail',
          message: 'Data URI scripts are allowed (security risk)',
        })
      }
      document.head.appendChild(script)

      setTimeout(() => {
        if (!results.find((r) => r.name === 'Data URI Script')) {
          results.push({
            name: 'Data URI Script',
            status: 'warning',
            message: 'Data URI script test timed out',
          })
        }
      }, 2000)
    } catch (error) {
      results.push({
        name: 'Data URI Script',
        status: 'pass',
        message: 'Data URI scripts are blocked by CSP',
        details: String(error),
      })
    }

    try {
      const iframe = document.createElement('iframe')
      iframe.src = 'about:blank'
      iframe.style.display = 'none'
      document.body.appendChild(iframe)

      results.push({
        name: 'Frame Embedding',
        status: 'pass',
        message: 'Frame embedding test completed (check frame-ancestors in CSP)',
      })
    } catch (error) {
      results.push({
        name: 'Frame Embedding',
        status: 'warning',
        message: 'Frame embedding test failed',
        details: String(error),
      })
    }

    setTestResults(results)
    setIsRunning(false)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass':
        return <CheckCircle size={16} color="green" />
      case 'fail':
        return <XCircle size={16} color="red" />
      case 'warning':
        return <AlertTriangle size={16} color="orange" />
      default:
        return <Clock size={16} color="gray" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass':
        return 'green'
      case 'fail':
        return 'red'
      case 'warning':
        return 'orange'
      default:
        return 'gray'
    }
  }

  useEffect(() => {
    runTests()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box p={8} maxW="4xl" mx="auto">
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={2}>
            Content Security Policy (CSP) Test Suite
          </Heading>
          <Text color={colors.text}>
            This page tests the CSP implementation to ensure security policies are working correctly.
          </Text>
        </Box>

        <HStack spacing={4}>
          <Button
            colorScheme="blue"
            onClick={runTests}
            isLoading={isRunning}
            loadingText="Running Tests..."
            leftIcon={<Clock size={16} />}
          >
            Run Tests Again
          </Button>
          <Badge colorScheme="blue" variant="outline">
            Development Mode
          </Badge>
        </HStack>

        {cspHeader && (
          <Alert status="info">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold" mb={2}>
                CSP Header Detected:
              </Text>
              <Code fontSize="xs" p={2} display="block" whiteSpace="pre-wrap">
                {cspHeader}
              </Code>
            </Box>
          </Alert>
        )}

        {testResults.length > 0 && (
          <Box>
            <Heading size="md" mb={4}>
              Test Results ({testResults.length} tests)
            </Heading>
            <VStack spacing={3} align="stretch">
              {testResults.map((result, index) => (
                <Box key={index} p={4} border="1px" borderColor={colors.border} borderRadius="md" bg="white">
                  <HStack spacing={3} mb={2}>
                    {getStatusIcon(result.status)}
                    <Text fontWeight="bold" flex={1}>
                      {result.name}
                    </Text>
                    <Badge colorScheme={getStatusColor(result.status)}>
                      {result.status.toUpperCase()}
                    </Badge>
                  </HStack>
                  <Text color={colors.heading} mb={1}>
                    {result.message}
                  </Text>
                  {result.details && (
                    <Code fontSize="xs" p={2} display="block" whiteSpace="pre-wrap" mt={2}>
                      {result.details}
                    </Code>
                  )}
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        <Alert status="info">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold" mb={2}>
              Expected Results in Development Mode:
            </Text>
            <VStack align="start" spacing={1}>
              <Text fontSize="sm">✅ Eval should be allowed (for dev)</Text>
              <Text fontSize="sm">✅ Inline styles should be allowed (for dev)</Text>
              <Text fontSize="sm">❌ External scripts should be blocked</Text>
              <Text fontSize="sm">❌ Data URI scripts should be blocked</Text>
              <Text fontSize="sm">✅ API calls should work</Text>
            </VStack>
          </Box>
        </Alert>
      </VStack>
    </Box>
  )
}

